<template>
  <modal
    v-if="show"
    width="600px"
    bodyPadding="0"
    :title="`${t('Add custom domain')} (${step}/2)`"
    @close="checkClose"
    :read-article-url="`https://docs.bemob.com/${locale}/custom-domains`"
  >
    <template v-if="step === 1">
      <div class="dialog-wrapper add-custom-domain">
        <div class="w100">
          <div class="horizontal-steps">
            <ui-radio-group
              v-model="step"
              styleType="horizontal-line"
              class="radio_group"
            >
              <ui-radio
                class="circle pl-12 pr-12"
                :value="1"
                :label="t('Setting the CNAME')"
              />
              <ui-radio
                class="circle pl-12 pr-12"
                :value="2"
                :label="t('Checking DNS')"
              />
            </ui-radio-group>
          </div>

          <div class="vertical-steps">
            <ul>
              <li>
                <p>1</p>
                <div class="vertical-steps__inner">
                  <span>{{
                    t(
                      `Read the instructions to create a CNAME record in your registrar's panel`,
                    )
                  }}</span>
                  <div class="d-flex align-items-end">
                    <ui-select
                      v-model="form.registar"
                      :label="t('Your registrar / DNS hosting provider')"
                      :items="registars"
                      item-text="name"
                      item-value="id"
                      :autocomplete="false"
                      :hideDetails="1"
                      class="flex-grow-1"
                    />

                    <ui-button
                      class="ml-8 btn-read"
                      :text="t('Read instruction')"
                      icon="fas fa-question-circle"
                      button-style="outline-accent"
                      :href="`https://docs.bemob.com/${locale}/custom-domain-settings`"
                      target="_blank"
                    />
                  </div>
                </div>
              </li>
              <li>
                <p>2</p>
                <div class="vertical-steps__inner">
                  <ui-copy-input
                    :label="t('Create CNAME record for your domain that points to:')"
                    :model-value="form.domain"
                  />
                </div>
              </li>
              <li>
                <p>3</p>
                <div class="vertical-steps__inner">
                  <span>{{ t('Your CNAME record should look like this:') }}</span>
                  <div class="card_block mb-12">
                    <ui-info>your.domain.name. CNAME {{ form.domain }}</ui-info>
                  </div>
                </div>
              </li>
              <li>
                <p>4</p>
                <div class="vertical-steps__inner">
                  <span class="d-flex">{{
                    t('Please note that updating DNS records may take up to 24 hours')
                  }}</span>
                  <span>{{ t('Usually it takes 15-20 minutes') }}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </template>

    <template v-if="step === 2">
      <div class="dialog-wrapper add-custom-domain">
        <div class="w100 pb-0">
          <div class="horizontal-steps">
            <ui-radio-group
              v-model="step"
              class="radio_group"
              styleType="horizontal-line"
            >
              <ui-radio
                class="circle pl-12 pr-12"
                :value="1"
                :label="t('Setting the CNAME')"
              />
              <ui-radio
                class="circle pl-12 pr-12"
                :value="2"
                :label="t('Checking DNS')"
              />
            </ui-radio-group>
          </div>
          <div class="vertical-steps vertical-steps__two">
            <div class="vertical-steps__inner p-0 mt-32">
              <span
                v-html="
                  t(
                    'Add your domain with no https:// or any other additional symbols e.g. www.mydomain.com<br>Then click on <b>Check DNS</b> button.',
                  )
                "
              />
              <div class="d-flex align-items-end mt-8">
                <ui-input
                  v-model="targetDomain"
                  :label="t('Your Domain')"
                  :placeholder="curentPlaceholder"
                  :hideDetails="1"
                  class="flex-grow-1"
                />

                <ui-button
                  class="ml-8 btn-read"
                  icon="fas fa-sync-alt"
                  :text="t('Check DNS')"
                  button-style="outline-accent"
                  :disabled="!targetDomain"
                  :loading="loadingDns"
                  @click="checkDns"
                />
              </div>
              <div class="d-flex success-block" v-if="domainCheckingSuccess">
                {{ t('Domain configured properly') }}. {{ t(`Press 'Next' to proceed`) }}.
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer v-if="step === 1">
      <section class="justify-end xs12" style="display: flex">
        <ui-button
          :text="t('Next')"
          icon-append="fas fa-arrow-right"
          button-style="accent"
          @click="step = 2"
        />
      </section>
    </template>

    <template #footer v-if="step === 2">
      <section class="justify-space-between xs12" style="display: flex; width: 100%">
        <div>
          <error-aggregator v-model="errors" v-model:active="errorsControlActive" />
        </div>
        <div class="justify-end d-flex">
          <ui-button :text="t('Back')" icon="fas fa-arrow-left" @click="step = 1" />

          <ui-button
            :text="t('Add Domain')"
            icon="fas fa-check"
            button-style="accent"
            :loading="loading"
            @click="createDomain"
          />
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import i18n from '@/i18n';
import { useStore } from '@/store';
import { useI18n } from '@/composables';
import { $emitter } from '@/services/emitter';
import md5 from 'md5';
import type { AggregatedErrorItem } from '@/components/global/error-aggregator.vue';
import parseApiErrors from '@/utils/parseApiErrors';
import {
  postCheckRedirectDomainDnsApiRequest,
  postCreateRedirectDomainApiRequest,
} from '@/services/api/domains-api';
import { populateRedirectDomainsStoreCacheApiRequest } from '@/services/api/api-services/domains';

defineProps<{
  data?: Record<string, unknown>;
}>();

const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();
const store = useStore();
const show = ref(false);
const loading = ref(false);
const loadingDns = ref(false);
const step = ref(1);
const curentPlaceholder = ref('hide.yourdomain.com');
const targetDomain = ref('');
const domainCheckingSuccess = ref(false);
const errors = ref<string | string[] | AggregatedErrorItem[] | null>(null);
const errorsControlActive = ref(false);
const oldHash = ref('');

const form = reactive({
  registar: 'namecheap',
  domain: '',
});

const registars = ref<{ id: string; name: string }[]>([]);

const locale = computed(() => i18n.global.locale.value);

const getCurrentHash = () => md5(JSON.stringify(form));

const clearErrors = () => {
  errors.value = [];
  errorsControlActive.value = false;
  domainCheckingSuccess.value = false;
};

const showApiValidationError = (error: unknown) => {
  const responseMessage = (
    error as { response?: { data?: { message?: string } } }
  ).response?.data?.message;
  const messages = Object.values(parseApiErrors({ error })).flat();
  const items: AggregatedErrorItem[] = [];
  if (responseMessage) {
    items.push({ message: t(String(responseMessage)) });
  }
  messages.forEach((message) => {
    items.push({ message });
  });
  if (!items.length) return;
  errors.value = items;
  errorsControlActive.value = true;
};

const checkDns = async () => {
  loadingDns.value = true;
  clearErrors();
  try {
    const data = (await postCheckRedirectDomainDnsApiRequest({
      domain: targetDomain.value,
    })) as {
      status?: string;
      error?: string;
      values?: { domain?: string; setTo?: string; shouldBe?: string };
    };
    if (data.status === 'FAIL') {
      errors.value = [
        {
          message: t(data.error ?? '', [
            data.values?.domain,
            data.values?.setTo,
            data.values?.shouldBe,
          ]),
        },
      ];
      errorsControlActive.value = true;
      return;
    }
    domainCheckingSuccess.value = true;
  } catch (error: unknown) {
    showApiValidationError(error);
  } finally {
    loadingDns.value = false;
  }
};

const checkClose = () => {
  if (oldHash.value !== getCurrentHash()) {
    $emitter.emit('open-dialog', {
      name: 'ConfirmDialog',
      data: { onConfirm: () => emit('close') },
    });
  } else {
    emit('close');
  }
};

const createDomain = async () => {
  loading.value = true;
  clearErrors();
  try {
    await postCreateRedirectDomainApiRequest({ domain: targetDomain.value });
    await populateRedirectDomainsStoreCacheApiRequest();
    emit('close');
    $emitter.emit('toast-success', t('Custom cloaking domain successfully added.'));
  } catch (error: unknown) {
    showApiValidationError(error);
  } finally {
    loading.value = false;
  }
};

watch(step, () => {
  clearErrors();
});

onMounted(() => {
  store.setLoading(true);
  const defaultDomainRow = store.redirectDomains.data.find(item => !item.custom);
  form.domain = defaultDomainRow?.domain ?? '';
  registars.value = [
    { id: 'namecheap', name: t('Namecheap') },
    { id: 'godaddy', name: t('GoDaddy') },
    { id: 'cloudflare', name: t('Cloudflare') },
    { id: 'other', name: t('Other') },
  ];
  show.value = true;
  oldHash.value = getCurrentHash();
  store.setLoading(false);
});
</script>

<style lang="scss" scoped>
@import '@/styles/pages/settings/domains';
@import '@/styles/dialogs/report-modals';

.add-custom-domain {
  .error-block {
    background: var(--color-red);
    color: var(--color-white);
    padding: 16px;
  }
  .success-block {
    background: var(--color-green);
    color: var(--color-white);
    padding: 16px;
  }
  > div {
    padding: 16px 24px 24px;
    max-height: 100%;
    overflow: auto;
  }
  .horizontal-steps {
    padding-bottom: 16px;
    border-bottom: 1px solid var(--color-gray20);
  }
  .vertical-steps {
    ul {
      list-style-type: none;
      padding-top: 16px;
      padding-left: 12px;
      > li {
        margin-bottom: -20px;
        p {
          position: relative;
          left: -12px;
          z-index: 1;
          display: inline-block;
          width: 24px;
          height: 24px;
          margin: 0;
          padding: 3px;
          text-align: center;
          border-radius: 20px;
          background: var(--color-gray20);
        }
      }
      li:last-child .vertical-steps__inner {
        border-left: 0;
      }
    }
    &__inner {
      position: relative;
      top: -20px;
      padding-left: 24px;
      padding-bottom: 16px;
      border-left: 1px solid var(--color-gray20);
      font-size: 13px;
      .v-text-field__details {
        display: none;
      }
      .btn-read {
        margin-right: 0;
        max-width: max-content;
        flex-shrink: 0 !important;
      }
      .copy-input-label {
        font-weight: normal;
      }
    }
  }
  .vertical-steps__two {
    .vertical-steps__inner {
      border: none;
    }
  }
}
</style>
